import React, { useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { useFormik } from 'formik'
import * as Yup from 'yup'

import EditForm from '../../../../../../features/components/Forms/EditForm'
import MultipleOptionsField from '../../MultipleOptionsField'
import { getInitialValuesByFormat } from '../Media/Edit/helpers'
import { getCreative } from './helpers'
import { isCustomAdWithStory } from '../../helpers'

import Field from '../../../../../../components/Form/Field'

import useEditAdPreview from '../../../../../../features/hooks/useEditAdPreview'
import { usePurifiedFormik } from '../../../../../../hooks/formHooks/usePurifiedFormik'

import { clearUpdateAd, updateAd } from '../../../../../../modules/actions/ads'

import {
  adUpdateErrorSelector,
  adUpdateIsLoadingSelector,
  adWasUpdatedSelector
} from '../../../../../../modules/selectors/ads'
import { lineItemSelector } from '../../../../../../modules/selectors/lineItems'
import { selectedFacebookAdAccountSelector } from '../../../../../../modules/selectors/adAccounts'

import { PRIMARY_TEXT_LIST } from '../../../fields'
import { primaryTextListValidation } from './validation'
import { FACEBOOK_PLATFORM } from '../../../../../../constants/selectLists/platformList'
import { HISTORY_META_EDIT_SECTION } from '../../../../../../constants/changeHistory'

const PrimaryTextForm = ({ editItemData: originalAd, adValues, adFormat, isInternalAd, title, ...formProps }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const lineItem = useSelector(lineItemSelector)
  const { id: selectedAdAccountId } = useSelector(selectedFacebookAdAccountSelector)
  const successUpdate = useSelector(adWasUpdatedSelector)

  const { ad_account_id: adAccountId } = originalAd
  const adAccount = adAccountId || selectedAdAccountId

  const handleSubmit = useCallback(
    values => {
      const { id } = originalAd

      const updateAdData = {
        account: adAccount,
        adset_id: lineItem.id,
        creative: {
          ...getCreative(values, originalAd, adFormat)
        },
        ...(isInternalAd && { internal: true }),
        meta: {
          [HISTORY_META_EDIT_SECTION]: title
        }
      }
      dispatch(updateAd(updateAdData, id, FACEBOOK_PLATFORM))
    },
    [dispatch, originalAd, adAccount, lineItem.id, adFormat, isInternalAd, title]
  )

  const validationSchema = Yup.object(primaryTextListValidation)

  const initialValues = useMemo(
    () => ({
      ...getInitialValuesByFormat(adValues, adFormat)
    }),
    [adValues, adFormat]
  )

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema,
    onSubmit: handleSubmit
  })
  const purifiedFormik = usePurifiedFormik(formik)
  const { values } = purifiedFormik
  useEditAdPreview(values)

  // Currently we support multiple primary texts only for custom ads with story,
  // There are other types of ads that support multiple primary texts, but we don't support them yet
  const allowMultiple = useMemo(() => isCustomAdWithStory(originalAd), [originalAd])

  const handleClearAd = useCallback(() => {
    dispatch(clearUpdateAd())
  }, [dispatch])

  return (
    <EditForm
      formik={purifiedFormik}
      initialValues={initialValues}
      successSubmit={successUpdate}
      clearEditItem={handleClearAd}
      errorSelector={adUpdateErrorSelector}
      isLoadingSelector={adUpdateIsLoadingSelector}
      {...formProps}
    >
      {allowMultiple ? (
        <>
          <p>{t('If you add multiple options here, Facebook will determine the best one to use.')}</p>
          <MultipleOptionsField
            formik={purifiedFormik}
            fieldPlaceholder="Primary Text"
            listFieldName={PRIMARY_TEXT_LIST}
          />
        </>
      ) : (
        <Field
          placeholder={t('Primary text')}
          formik={formik}
          name={`${PRIMARY_TEXT_LIST}[0].text`}
          isTextarea
          enableReinitialize
        />
      )}
    </EditForm>
  )
}

PrimaryTextForm.propTypes = {
  editItemData: PropTypes.object,
  adValues: PropTypes.object,
  adFormat: PropTypes.string,
  isInternalAd: PropTypes.bool
}

export default PrimaryTextForm
